'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Calendar, ChevronRight, AlertTriangle, TrendingUp, Loader2 } from 'lucide-react';

interface YearStats {
  year: number;
  count: number;
  criticalCount: number;
  highCount: number;
}

export default function YearBrowseClient() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yearStats, setYearStats] = useState<YearStats[]>([]);

  useEffect(() => {
    async function fetchYearStats() {
      try {
        const response = await fetch('/api/tools/cve/year');
        if (!response.ok) {
          throw new Error('Failed to fetch year data');
        }
        const data = await response.json();
        if (data.error) {
          throw new Error(data.error);
        }
        setYearStats(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }
    fetchYearStats();
  }, []);

  const totalCVEs = yearStats.reduce((sum, y) => sum + y.count, 0);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading year data...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-red-800 dark:text-red-200 mb-2">
          Unable to Load Data
        </h2>
        <p className="text-red-700 dark:text-red-300">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary Stats */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <Calendar className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          <h2 className="text-xl font-bold text-blue-900 dark:text-blue-100">
            CVE Archive Overview
          </h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">
              {totalCVEs.toLocaleString()}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">Total CVEs</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">
              {yearStats.length}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">Years Covered</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">
              {yearStats[0]?.year || '-'}
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">Most Recent Year</div>
          </div>
        </div>
      </div>

      {/* Year List */}
      <div className="space-y-3">
        {yearStats.map((yearStat) => (
          <Link
            key={yearStat.year}
            href={`/tools/security/cve-lookup/year/${yearStat.year}`}
            className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-400 dark:hover:border-blue-600 hover:shadow-md transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">{yearStat.year}</span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                  CVE-{yearStat.year} Vulnerabilities
                </h3>
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                  <span className="font-medium">{yearStat.count.toLocaleString()} CVEs</span>
                  {yearStat.criticalCount > 0 && (
                    <span className="flex items-center gap-1 text-red-600 dark:text-red-400">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {yearStat.criticalCount.toLocaleString()} Critical
                    </span>
                  )}
                  {yearStat.highCount > 0 && (
                    <span className="flex items-center gap-1 text-orange-600 dark:text-orange-400">
                      <TrendingUp className="w-3.5 h-3.5" />
                      {yearStat.highCount.toLocaleString()} High
                    </span>
                  )}
                </div>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
          </Link>
        ))}
      </div>

      {yearStats.length === 0 && (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            No CVE Data Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            CVE archive data is currently being loaded. Please try again later.
          </p>
        </div>
      )}
    </div>
  );
}
